import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { chapterContent, chapterList } from '@/mocks/chapters';
import { comics } from '@/mocks/comics';

export default function ChapterPage() {
  const { comicId, chapterNum } = useParams<{ comicId: string; chapterNum: string }>();
  const currentChapter = parseInt(chapterNum || '186', 10);

  const comicData = comics.find((c) => c.id === parseInt(comicId || '1', 10));
  const comicTitle = comicData?.title || '洞洞杂货铺';

  const prevChapter = currentChapter > 1 ? currentChapter - 1 : null;
  const nextChapter = currentChapter < chapterContent.totalChapters ? currentChapter + 1 : null;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.dongdongcomic.com/' },
      { '@type': 'ListItem', position: 2, name: comicTitle, item: `https://www.dongdongcomic.com/category/${comicData?.category || 'qihuan'}` },
      { '@type': 'ListItem', position: 3, name: `第${currentChapter}话` },
    ],
  };

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comicTitle} - 第${currentChapter}话`,
    description: `${comicTitle}第${currentChapter}话完整内容，洞洞杂货铺漫画全集免费阅读。`,
    url: `https://www.dongdongcomic.com/chapter/${comicId}/${currentChapter}`,
    author: { '@type': 'Person', name: comicData?.author || '洞洞杂货铺' },
    datePublished: '2026-08-03',
    publisher: { '@type': 'Organization', name: '洞洞杂货铺', url: 'https://www.dongdongcomic.com' },
  };

  return (
    <>
      <PageMeta
        title={`${comicTitle}第${currentChapter}话-洞洞杂货铺漫画全集免费阅读`}
        description={`${comicTitle}第${currentChapter}话完整内容免费阅读，洞洞杂货铺漫画全集在线解锁，无需付费即可畅享。`}
        keywords={`${comicTitle},第${currentChapter}话,洞洞杂货铺,漫画全集,免费阅读,在线解锁`}
        canonicalUrl={`https://www.dongdongcomic.com/chapter/${comicId}/${currentChapter}`}
        jsonLd={[breadcrumbLd, articleLd]}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="px-4 md:px-6 max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comicTitle, href: `/category/${comicData?.category || 'qihuan'}` },
                { label: `第${currentChapter}话` },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8">
              <article className="flex-1 bg-background-100 rounded-xl p-6 md:p-10">
                <header className="mb-8 pb-6 border-b border-background-200/70">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    {comicTitle}
                  </h1>
                  <p className="text-lg text-primary-500 font-medium">
                    第{currentChapter}话 {chapterContent.chapterTitle.replace(`第${currentChapter}话 `, '')}
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500 flex-wrap">
                    <span>作者：{comicData?.author}</span>
                    <span>更新：2026-08-03</span>
                    <span>阅读：{(comicData?.views || 0) > 10000 ? `${((comicData?.views || 0) / 10000).toFixed(0)}万` : comicData?.views}</span>
                  </div>
                </header>

                <div
                  className="max-w-none text-foreground-800 leading-relaxed text-sm md:text-base"
                  dangerouslySetInnerHTML={{ __html: chapterContent.content }}
                />

                <div className="flex items-center justify-between mt-10 pt-6 border-t border-background-200/70">
                  {prevChapter ? (
                    <Link
                      to={`/chapter/${comicId}/${prevChapter}`}
                      className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i> 上一话
                    </Link>
                  ) : (
                    <span className="text-sm text-foreground-300 whitespace-nowrap">
                      <i className="ri-arrow-left-line"></i> 上一话
                    </span>
                  )}

                  <Link
                    to={`/category/${comicData?.category || 'qihuan'}`}
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    返回目录
                  </Link>

                  {nextChapter ? (
                    <Link
                      to={`/chapter/${comicId}/${nextChapter}`}
                      className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      下一话 <i className="ri-arrow-right-line"></i>
                    </Link>
                  ) : (
                    <span className="text-sm text-foreground-300 whitespace-nowrap">
                      下一话 <i className="ri-arrow-right-line"></i>
                    </span>
                  )}
                </div>
              </article>

              <aside className="w-full lg:w-[280px] flex-shrink-0">
                <div className="bg-background-100 rounded-xl p-5 sticky top-24">
                  <h3 className="text-base font-bold text-foreground-900 mb-4">章节列表</h3>
                  <div className="space-y-1 max-h-[500px] overflow-y-auto">
                    {chapterList.map((ch) => (
                      <Link
                        key={ch.number}
                        to={`/chapter/${comicId}/${ch.number}`}
                        className={`block text-sm px-3 py-2 rounded-md transition-colors duration-200 whitespace-nowrap ${
                          ch.number === currentChapter
                            ? 'bg-primary-100 text-primary-600 font-medium'
                            : 'text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {ch.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}